/**
 * a2ui WIDGET wrappers (DEC-6) — the catalog-driven mapping from an a2ui
 * (`view_ir:2`) widget view back onto the EXISTING viewIr kit.
 *
 * DESIGN (the smart wrapper): the widget data lane lives at the HOST
 * (`buildRenderContext`), and every widget renders through the ONE oracle
 * interpreter (`ViewIRPage`) + its React components. So the web a2ui widget
 * runtime reconstructs the viewIr blueprint from the a2ui view — the exact
 * inverse of `convert.js` — and hands it to the existing render path. Because
 * forward+inverse are a bijection on the emitted shapes, a converted widget
 * renders byte-identically to its `view_ir:1` twin (the parity guarantee the
 * drift panel + parity tests check). `components.js` owns the component-name
 * mapping + prop adapters; `functions.js` owns the expression/connection inverse.
 */

import { CATALOG_META, a2uiToViewIrType, EXTENSION_TO_COMPONENT } from './catalog.js';
import {
  dynamicToBind, visibleToWhen, actionToConnection, pointerToDotted,
} from './functions.js';

const HOIST_SUFFIX_RE = /__(card|cell|center|from|to|child\d+|item)$/;

/**
 * widgets-formalize TARGET FORM → the origin a2ui node (the inverse of
 * convert.js `wrapAsTarget`). A core host carrying a `collov_*` re-key payload
 * (its key maps to an origin component via EXTENSION_TO_COMPONENT) OR a
 * `vi_origin` marker (a pure-core drop) is rebuilt AS the origin component with
 * the payload's props, so our kit renders the familiar custom body — render
 * parity by construction. A default-form node (no such key) returns null and
 * reconstructs normally. Session-only keys (collov_shopping_list, …) are not in
 * EXTENSION_TO_COMPONENT, so they never re-key here.
 */
function detectTargetRekey(comp) {
  const ext = comp && comp.metadata && comp.metadata.extensions;
  if (!ext || typeof ext !== 'object') return null;
  if (ext.vi_origin && typeof ext.vi_origin === 'object' && typeof ext.vi_origin.component === 'string') {
    const { component, ...rest } = ext.vi_origin;
    return mergeOriginNode(comp, component, rest);
  }
  for (const [k, v] of Object.entries(ext)) {
    const origin = EXTENSION_TO_COMPONENT[k];
    if (origin && v && typeof v === 'object' && !Array.isArray(v)) return mergeOriginNode(comp, origin, v);
  }
  return null;
}

/**
 * The origin a2ui node: the wrapped payload (which already carries the ORIGIN's
 * own props + children) under the host's id. The host's core props and its
 * EXPANDED core children (third-party fodder) are deliberately ignored — the
 * payload is the source of truth for our kit, so re-key is byte-exact.
 */
function mergeOriginNode(comp, originComponent, payload) {
  return { ...payload, id: comp.id, component: originComponent };
}

/** Un-rename a core-superset a2ui prop back to its viewIr name. */
function unrenameProp(type, key) {
  if (type === 'Text' && key === 'text') return 'value';
  if ((type === 'Image' || type === 'Video') && key === 'url') return 'src';
  if (type === 'Image' && key === 'description') return 'alt';
  if (type === 'Video' && key === 'posterUrl') return 'poster';
  return key;
}

/** viewIr node types whose a2ui content prop actually came from a node-level bind. */
const BIND_BACK = { Image: 'url', Video: 'url' };

/**
 * A NODE-LEVEL `bind` is a selector NAME, and both kits type it as a plain
 * string (`ViewIrNode.bind` is `String?`; Dart's `viewIrString` returns null for
 * a map). `dynamicToBind` is the inverse for prop VALUES and yields `{$: path}`,
 * which silently becomes "no data source" — an ItemCollection bound that way
 * renders its empty state instead of its rows. Flutter already goes through
 * `dynamicToBindString`; this is the web twin of it.
 */
function dynamicToNodeBind(dyn) {
  const back = dynamicToBind(dyn);
  if (typeof back === 'string') return back;
  if (back && typeof back === 'object' && typeof back.$ === 'string') return back.$;
  return back;
}

/**
 * Reconstruct the full ViewIR (`view_ir:1`) blueprint from an a2ui widget view.
 * Deterministic inverse of `convertBlueprint`.
 */
export function a2uiViewToBlueprint(view) {
  if (!view || Number(view.view_ir) === 1) return view; // already a blueprint
  const allComps = {};
  const surfaces = view.surfaces || [];
  // index every a2ui component by id across surfaces
  for (const s of surfaces) for (const c of s.createSurface?.components || []) allComps[c.id === 'root' ? `${s.page}__root` : c.id] = { ...c, _page: s.page };

  const connections = [];
  const components = {};

  const rebuildNode = (comp) => {
    // TARGET FORM → origin node (then reconstruct that); default form → null.
    const origin = detectTargetRekey(comp);
    if (origin) return rebuildNode(origin);
    const type = a2uiToViewIrType(comp.component);
    const node = { type };
    const props = {};
    const ext = comp.metadata?.extensions || {};

    for (const [k, v] of Object.entries(comp)) {
      if (k === 'id' || k === 'component' || k === 'metadata' || k === '_page') continue;
      if (k === 'visible') { const w = visibleToWhen(v); if (w !== undefined) node.when = w; continue; }
      if (k === 'region') { node.region = v; continue; }
      if (k === 'variant') { node.variant = v; continue; }
      if (k === 'children') { node.children = rebuildChildren(comp.id, v); continue; }
      if (/^on_[A-Za-z0-9_]+$/.test(k)) continue; // → connections below
      if (k === 'bind') { node.bind = dynamicToNodeBind(v); continue; } // untyped custom bind
      // hoisted inline-node prop: a string id pointing at `<id>__<prop>`
      if (typeof v === 'string' && HOIST_SUFFIX_RE.test(v) && allComps[v]) {
        props[k] = rebuildNode(allComps[v]);
        continue;
      }
      // Image/Video content bind: a Dynamic in the content prop came from node.bind
      if (BIND_BACK[type] === k && v && typeof v === 'object') { node.bind = dynamicToNodeBind(v); continue; }
      props[unrenameProp(type, k)] = valueBack(v);
    }

    // metadata-carried viewIr fields
    if (ext.vi_variant != null) node.variant = ext.vi_variant;
    if (ext.vi_bind != null) node.bind = dynamicToNodeBind(ext.vi_bind);
    if (ext.vi_props && typeof ext.vi_props === 'object') {
      for (const [k, v] of Object.entries(ext.vi_props)) props[k] = valueBack(v);
    }

    if (Object.keys(props).length) node.props = props;

    // connections from on_<signal> ActionLists
    for (const [k, v] of Object.entries(comp)) {
      const m = /^on_([A-Za-z0-9_]+)$/.exec(k);
      if (!m || !Array.isArray(v)) continue;
      for (const action of v) {
        const conn = actionToConnection(comp.id === 'root' ? `${comp._page}__root` : comp.id, m[1], action);
        if (conn) connections.push(conn);
      }
    }

    // HOST-INBOUND wires, restored from the page root's `vi_host_on`. Their
    // source is the HOST, not this component — `host.dataReceived` is what the
    // kit matches when the host pushes new data, so rebuilding them under the
    // root's id would leave the page just as dead as dropping them.
    const hostWires = ext.vi_host_on;
    if (hostWires && typeof hostWires === 'object') {
      for (const [sig, actions] of Object.entries(hostWires)) {
        if (!Array.isArray(actions)) continue;
        for (const action of actions) {
          const conn = actionToConnection('host', sig, action);
          if (conn) connections.push(conn);
        }
      }
    }
    return node;
  };

  const rebuildChildren = (parentId, spec) => {
    if (Array.isArray(spec)) {
      return spec.map((cid) => {
        if (typeof cid === 'string' && HOIST_SUFFIX_RE.test(cid) && allComps[cid]) return rebuildNode(allComps[cid]);
        return cid; // id-ref (resolved from the global map)
      });
    }
    if (spec && typeof spec === 'object' && spec.componentId) {
      // ChildList template → viewIr repeat; the template component is the row body
      return undefined; // handled via node.repeat in the caller path below
    }
    return undefined;
  };

  // emit every NON-hoisted component into the global map
  for (const [id, comp] of Object.entries(allComps)) {
    const bareId = comp.id;
    if (HOIST_SUFFIX_RE.test(bareId)) continue; // inlined into its parent
    const node = rebuildNode(comp);
    // repeat template: children was {componentId, path}. The template component
    // IS the row body, and it is hoisted (`<id>__item`) so it is never emitted
    // into the global map on its own — reconstructing only `repeat` and dropping
    // the template left a repeat with NOTHING to repeat, so the whole section
    // rendered empty (and any consumer reading `items` off it saw a non-list).
    if (Array.isArray(comp.children) === false && comp.children && comp.children.componentId) {
      node.repeat = pointerToDotted(comp.children.path);
      const cid = comp.children.componentId;
      if (HOIST_SUFFIX_RE.test(cid) && allComps[cid]) node.children = [rebuildNode(allComps[cid])];
      else if (allComps[cid]) node.children = [cid];
    }
    components[id] = node;
  }

  // `page_file` is what the host routes by (`pageForFile`), and it is NOT
  // derivable from the page id — `detail` ships as `data_view.html`. A doc
  // converted before the field was carried has none, in which case the entry
  // page keeps the historic default and the others fall back to `<id>.html`
  // rather than silently collapsing onto `index.html` (which made every
  // subpage render the entry page).
  // `page_file` PRESENT (null included) is the converter's own value and is kept
  // verbatim: a null one is deliberate — the kit defaults such a page to the
  // entry file — and inventing `<page>.html` for it routes the same page
  // differently than its v1 twin. The fallback is only for a document converted
  // before the field was carried at all, where the key is genuinely absent.
  const pages = surfaces.map((s) => ({
    id: s.page,
    entry: s.entry === true,
    page_file: 'page_file' in s
      ? s.page_file
      : (s.entry === true ? 'index.html' : `${s.page}.html`),
    root: `${s.page}__root`,
  }));

  const blueprint = {
    view_ir: 1,
    widget_id: view.widget_id,
    title: view.title,
    // The AUTHORED floor when the document carries it: the kit reads
    // `app_min_version` as a layout switch (no page gutter at >= 0.9.7), and the
    // a2ui row's serving floor is forced to 0.10.0, so restoring the serving
    // value here silently restyles every migrated widget.
    app_min_version: view.vi_authored_app_min_version ?? view.app_min_version,
    pages,
    components,
    connections,
  };
  if (view.accent != null) blueprint.accent = view.accent;
  if (view.data && typeof view.data === 'object') blueprint.data = structuredClone(view.data);
  return blueprint;
}

/** Deep-invert a converted prop VALUE (Dynamic → viewIr bind grammar). */
function valueBack(v) {
  if (v == null) return v;
  if (typeof v !== 'object') return v;
  if (Array.isArray(v)) return v.map(valueBack);
  if ('path' in v || 'call' in v) return dynamicToBind(v);
  const out = {};
  for (const [k, val] of Object.entries(v)) out[k] = valueBack(val);
  return out;
}

/** The canonical a2ui component names this wrapper knows (for the studio + docs). */
export const WIDGET_COMPONENTS = Object.keys(CATALOG_META);
